/**
 * Google Analytics 4 (gtag.js) integration.
 *
 * - Loads only when `enabled` is true AND a measurement ID is configured, so
 *   local, test, feature-branch and QA builds never send data.
 * - Consent Mode v2: when `consentGated` is true the tag starts with
 *   `analytics_storage: denied` until the visitor accepts via the cookie
 *   banner (`updateAnalyticsConsent`). Consent is persisted in localStorage
 *   under `cookieConsent` — the same key the CookieConsent banner writes — so
 *   apps served from the same origin share the visitor's choice.
 * - Advertising storage is always denied: the platform runs no ads.
 * - `trackPageView` sends the pathname only (never the query string), because
 *   session tokens can travel as URL parameters on deep links.
 */

const CONSENT_STORAGE_KEY = "cookieConsent";
const GTAG_SRC = "https://www.googletagmanager.com/gtag/js";

type ConsentValue = "granted" | "denied";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export interface InitAnalyticsOptions {
  /** GA4 web stream measurement ID, e.g. `G-XXXXXXXXXX`. */
  measurementId?: string;
  /** Master switch — pass `false` outside production. */
  enabled: boolean;
  /** Start with analytics storage denied until the visitor consents. */
  consentGated: boolean;
  /**
   * Let gtag send the initial `page_view` itself. Keep `true` for
   * server-rendered pages with full reloads; set `false` in SPAs and call
   * `trackPageView` on every route change instead.
   */
  sendInitialPageView?: boolean;
}

let initialised = false;
let activeMeasurementId: string | undefined;

// gtag.js requires the raw `arguments` object on the dataLayer — a plain
// array is silently ignored — hence the untyped function and the cast.
const gtag = function () {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
} as (...args: unknown[]) => void;

/** `true` / `false` when the visitor already chose, `null` when not yet asked. */
export function readStoredAnalyticsConsent(): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && "analytics" in parsed) {
      return Boolean((parsed as { analytics?: unknown }).analytics);
    }
    return null;
  } catch {
    return null;
  }
}

export function isAnalyticsActive(): boolean {
  return initialised;
}

export function initAnalytics(options: InitAnalyticsOptions): void {
  if (typeof window === "undefined" || initialised) return;
  const {
    measurementId,
    enabled,
    consentGated,
    sendInitialPageView = false,
  } = options;
  if (!enabled || !measurementId) return;

  initialised = true;
  activeMeasurementId = measurementId;
  window.gtag = gtag;
  gtag("js", new Date());

  if (consentGated) {
    const stored = readStoredAnalyticsConsent();
    const analytics: ConsentValue = stored ? "granted" : "denied";
    gtag("consent", "default", {
      analytics_storage: analytics,
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      wait_for_update: 500,
    });
  }

  gtag("config", measurementId, {
    send_page_view: sendInitialPageView,
    anonymize_ip: true,
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `${GTAG_SRC}?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
}

/** Call from the cookie banner whenever the visitor saves a choice. */
export function updateAnalyticsConsent(granted: boolean): void {
  if (!initialised) return;
  gtag("consent", "update", {
    analytics_storage: granted ? "granted" : "denied",
  });
}

export function trackPageView(pathname: string, title?: string): void {
  if (!initialised || !activeMeasurementId) return;
  gtag("event", "page_view", {
    page_path: pathname,
    page_location: `${window.location.origin}${pathname}`,
    ...(title ? { page_title: title } : {}),
  });
}

export function trackEvent(
  name: string,
  params: Record<string, string | number | boolean> = {}
): void {
  if (!initialised) return;
  gtag("event", name, params);
}

/** Test-only: reset module state between specs. */
export function __resetAnalyticsForTests(): void {
  initialised = false;
  activeMeasurementId = undefined;
  if (typeof window !== "undefined") {
    delete window.gtag;
    window.dataLayer = [];
  }
}
